<template>
  <v-card outlined class="mx-auto">
    <v-card-subtitle
      class="font-weight-bold"
    >
      <div class="d-flex flex-column flex-sm-row justify-space-between align-sm-center">
        <div class="d-flex flex-column align-start mb-2 mb-sm-0">
          <div class="d-flex align-center">
            {{ $vuetify.lang.t("$vuetify.questionnaire_responses.list_title") }}
            &nbsp;
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-icon small v-bind="attrs" v-on="on">
                  mdi-help-circle-outline
                </v-icon>
              </template>
              <span>
                {{
                  $vuetify.lang.t("$vuetify.questionnaire_responses.list_hint")
                }}
              </span>
            </v-tooltip>
          </div>
          <div class="d-flex align-center flex-wrap mt-2">
            <v-chip small color="#A9A9A9" text-color="white" label>
              {{
                $vuetify.lang.t(
                  "$vuetify.questionnaire_responses.total_responses"
                )
              }}: {{ totalHits }}
            </v-chip>
          </div>
        </div>
        <div class="d-flex flex-row align-center mt-2">
          <v-btn
            class="mr-1"
            small
            color="primary"
            @click="fileGenerationModal = true"
          >
            <v-icon left small>mdi-download</v-icon>
            <span class="hidden-sm-and-down">
              {{ $vuetify.lang.t("$vuetify.common.csv") }}
            </span>
          </v-btn>
          <v-btn
            class="ml-1"
            small
            color="primary"
            @click="fileHistoryModal = true"
          >
            <v-icon left small>mdi-history</v-icon>
            <span class="hidden-sm-and-down">
              {{ $vuetify.lang.t("$vuetify.common.history") }}
            </span>
          </v-btn>
        </div>
      </div>
    </v-card-subtitle>
    <v-divider></v-divider>

    <v-row class="d-flex flex-column flex-md-row py-4 mb-n6 px-6">
      <v-col class="pa-0 pr-0 pr-md-3">
        <v-text-field
          v-model="searchText"
          :label="
            $vuetify.lang.t('$vuetify.questionnaire_responses.search_text')
          "
          outlined
          dense
          clearable
        ></v-text-field>
      </v-col>
      <v-col class="pa-0 pr-0 pr-md-3">
        <v-text-field
          v-model="searchByUserId"
          :label="
            $vuetify.lang.t('$vuetify.questionnaire_responses.search_by_user_id')
          "
          outlined
          dense
          clearable
        ></v-text-field>
      </v-col>
      <v-col class="pa-0 pr-0 pr-md-3">
        <v-text-field
          v-model="searchByMemberId"
          :label="
            $vuetify.lang.t(
              '$vuetify.questionnaire_responses.search_by_member_id'
            )
          "
          outlined
          dense
          clearable
        ></v-text-field>
      </v-col>
      <v-col class="pa-0 pr-0 pr-md-3">
        <v-text-field
          v-model="searchBySessionId"
          :label="
            $vuetify.lang.t(
              '$vuetify.questionnaire_responses.search_by_session_id'
            )
          "
          outlined
          dense
          clearable
        ></v-text-field>
      </v-col>
      <v-col class="pa-0 pr-0 pr-md-3">
        <v-combobox
          v-model="selectedQuestionnaires"
          :items="questionnaires"
          item-text="name"
          item-value="id"
          :label="
            $vuetify.lang.t(
              '$vuetify.questionnaire_responses.filter_by_questionnaire'
            )
          "
          outlined
          dense
          clearable
          multiple
          small-chips
          deletable-chips
          return-object
          :loading="isQuestionnaireListLoading"
          :menu-props="{ contentClass: 'custom-zindex' }"
          @change="handleQuestionnaireChange"
        >
          <template #selection="{ attrs, item, selected }">
            <thin-scroll-chip
              :item="item"
              :selected="selected"
              v-bind="attrs"
              @close="removeQuestionnaire(item)"
            />
          </template>
          <template v-if="hasMoreQuestionnaires" #append-item>
            <v-list-item>
              <v-list-item-content>
                <div class="d-flex">
                  <v-btn
                    class="ma-0 pa-0"
                    color="primary"
                    text
                    @click="getQuestionnaires()"
                  >
                    {{ $vuetify.lang.t("$vuetify.common.load_more") }}
                  </v-btn>
                </div>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-combobox>
      </v-col>
      <v-col class="pa-0 pr-0 pr-md-3">
        <v-combobox
          v-model="selectedProfiles"
          :items="gptProfiles"
          item-text="name"
          item-value="id"
          :label="
            $vuetify.lang.t(
              '$vuetify.questionnaire_responses.filter_by_profile'
            )
          "
          outlined
          dense
          clearable
          multiple
          small-chips
          deletable-chips
          return-object
          :loading="isProfileListLoading"
          :menu-props="{ contentClass: 'custom-zindex' }"
          @change="handleProfileChange"
        >
          <template #selection="{ attrs, item, selected }">
            <thin-scroll-chip
              :item="item"
              :selected="selected"
              v-bind="attrs"
              @close="removeProfile(item)"
            />
          </template>
          <template v-if="hasMoreProfiles" #append-item>
            <v-list-item>
              <v-list-item-content>
                <div class="d-flex">
                  <v-btn
                    class="ma-0 pa-0"
                    color="primary"
                    text
                    @click="getGPTProfiles()"
                  >
                    {{ $vuetify.lang.t("$vuetify.common.load_more") }}
                  </v-btn>
                </div>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-combobox>
      </v-col>
    </v-row>
    <v-divider></v-divider>

    <!-- Response Table start -->
    <v-data-table
      :headers="responseListHeaders"
      :items="responseList"
      :loading="isLoading"
      :loading-text="$vuetify.lang.t('$vuetify.common.loading_text')"
      :server-items-length="totalHits"
      :items-per-page="itemsPerPage"
      :page="page"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      item-key="response_id"
      :footer-props="{ showFirstLastPage: true }"
      @update:page="page = $event"
      @update:items-per-page="itemsPerPage = $event"
    >
      <template #item.response_id="{ item }">
        <div class="d-flex align-center">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                {{ item.response_id ? item.response_id.split("-")[0] : "" }}
              </span>
            </template>
            <span>{{ item.response_id || "N/A" }}</span>
          </v-tooltip>
          <v-tooltip v-if="item.response_id" bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                icon
                x-small
                class="ml-1"
                v-on="on"
                @click.stop="copyToClipboard(item, item.response_id, 'response_id')"
              >
                <v-icon
                  x-small
                  :color="
                    copiedField.id === item.response_id && copiedField.field === 'response_id'
                      ? 'success'
                      : undefined
                  "
                >
                  {{
                    copiedField.id === item.response_id && copiedField.field === 'response_id'
                      ? "mdi-check"
                      : "mdi-content-copy"
                  }}
                </v-icon>
              </v-btn>
            </template>
            <span>
              {{
                copiedField.id === item.response_id && copiedField.field === 'response_id'
                  ? $vuetify.lang.t("$vuetify.common.copied_to_clipboard")
                  : $vuetify.lang.t("$vuetify.common.copy")
              }}
            </span>
          </v-tooltip>
        </div>
      </template>
      <template #item.questionnaire_name="{ item }">
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <div
              v-bind="attrs"
              class="d-block text-truncate"
              style="max-width: 180px"
              v-on="on"
            >
              {{ item.questionnaire_name || "N/A" }}
            </div>
          </template>
          <span>{{ item.questionnaire_name || "N/A" }}</span>
        </v-tooltip>
      </template>
      <template #item.profile_name="{ item }">
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <div
              v-bind="attrs"
              class="d-block text-truncate"
              style="max-width: 160px"
              v-on="on"
            >
              {{ item.profile_name || "N/A" }}
            </div>
          </template>
          <span>{{ item.profile_name || "N/A" }}</span>
        </v-tooltip>
      </template>
      <template #item.user_name="{ item }">
        <span>{{ item.user_name || "N/A" }}</span>
      </template>
      <template #item.session_id="{ item }">
        <div class="d-flex align-center">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                {{ item.session_id ? item.session_id.split("-")[0] : "N/A" }}
              </span>
            </template>
            <span>{{ item.session_id || "N/A" }}</span>
          </v-tooltip>
          <v-tooltip v-if="item.session_id" bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                icon
                x-small
                class="ml-1"
                v-on="on"
                @click.stop="copyToClipboard(item, item.session_id, 'session_id')"
              >
                <v-icon
                  x-small
                  :color="
                    copiedField.id === item.response_id && copiedField.field === 'session_id'
                      ? 'success'
                      : undefined
                  "
                >
                  {{
                    copiedField.id === item.response_id && copiedField.field === 'session_id'
                      ? "mdi-check"
                      : "mdi-content-copy"
                  }}
                </v-icon>
              </v-btn>
            </template>
            <span>
              {{
                copiedField.id === item.response_id && copiedField.field === 'session_id'
                  ? $vuetify.lang.t("$vuetify.common.copied_to_clipboard")
                  : $vuetify.lang.t("$vuetify.common.copy")
              }}
            </span>
          </v-tooltip>
        </div>
      </template>
      <template #item.created_at="{ item }">
        {{ item.created_at ? formatDate(item.created_at) : "N/A" }}
      </template>
      <template #item.updated_at="{ item }">
        {{ item.updated_at ? formatDate(item.updated_at) : "N/A" }}
      </template>
      <template #[`item.actions`]="{ item }">
        <ButtonGroup
          :group-type="ACTION_BUTTON_GROUP"
          :details-button="true"
          :edit-button="true"
          :add-button="false"
          :start-button="false"
          :stop-button="false"
          :delete-button="false"
          :disabled-edit-button="!$hasProjectAccessFor([$pRole.ADMIN]) || item.session_status === 'active'"
          @onDetailsClick="openViewModal(item)"
          @onEditClick="openEditModal(item)"
        />
      </template>
    </v-data-table>
    <!-- Response Table end -->

    <!-- View Modal start -->
    <template v-if="viewModal">
      <v-dialog v-model="viewModal" max-width="820px" scrollable>
        <v-card>
          <v-card-title class="response-details-card-title">
            <span class="headline">
              <strong
                >{{
                  $vuetify.lang.t(
                    "$vuetify.questionnaire_responses.response_id"
                  )
                }}:</strong
              >
              {{ detail.response_id }}
            </span>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text style="max-height: 70vh">
            <template v-if="isDetailLoading">
              <Loader class="mr-2 mt-6" />
            </template>
            <template v-else>
              <v-row class="mt-1">
                <v-col cols="12" md="6">
                  <div>
                    <strong
                      >{{
                        $vuetify.lang.t(
                          "$vuetify.questionnaire_responses.questionnaire_name"
                        )
                      }}:</strong
                    >
                    {{ detail.questionnaire_name || "N/A" }}
                  </div>
                  <div>
                    <strong
                      >{{
                        $vuetify.lang.t(
                          "$vuetify.questionnaire_responses.member_id"
                        )
                      }}:</strong
                    >
                    {{ detail.member_id || "N/A" }}
                  </div>
                  <div>
                    <strong
                      >{{
                        $vuetify.lang.t(
                          "$vuetify.questionnaire_responses.user_id"
                        )
                      }}:</strong
                    >
                    {{ detail.user_id || "N/A" }}
                  </div>
                  <div>
                    <strong
                      >{{
                        $vuetify.lang.t(
                          "$vuetify.questionnaire_responses.user_name"
                        )
                      }}:</strong
                    >
                    {{ detail.user_name || "N/A" }}
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div>
                    <strong
                      >{{
                        $vuetify.lang.t(
                          "$vuetify.questionnaire_responses.profile_name"
                        )
                      }}:</strong
                    >
                    {{ detail.profile_name || "N/A" }}
                  </div>
                  <div>
                    <strong
                      >{{
                        $vuetify.lang.t(
                          "$vuetify.questionnaire_responses.session_id"
                        )
                      }}:</strong
                    >
                    {{ detail.session_id || "N/A" }}
                  </div>
                  <div>
                    <strong
                      >{{
                        $vuetify.lang.t(
                          "$vuetify.questionnaire_responses.created_at"
                        )
                      }}:</strong
                    >
                    {{
                      detail.created_at ? formatDate(detail.created_at) : "N/A"
                    }}
                  </div>
                  <div>
                    <strong
                      >{{
                        $vuetify.lang.t(
                          "$vuetify.questionnaire_responses.updated_at"
                        )
                      }}:</strong
                    >
                    {{
                      detail.updated_at ? formatDate(detail.updated_at) : "N/A"
                    }}
                  </div>
                </v-col>
              </v-row>
              <v-divider class="my-3"></v-divider>
              <div class="text-subtitle-1 font-weight-bold mb-2">
                {{
                  $vuetify.lang.t(
                    "$vuetify.questionnaire_responses.questions_and_answers"
                  )
                }}
              </div>
              <v-card
                v-for="q in filteredDetailQuestions"
                :key="q.question_key"
                outlined
                class="mb-3 pa-3"
              >
                <div class="font-weight-medium">
                  {{ q.question || q.question_key }}
                </div>
                <div class="text--secondary answer-type-text mb-1">
                  {{ q.type }}
                </div>
                <div v-if="q.answered">{{ formatAnswer(q.answer) }}</div>
                <div v-else class="text--disabled">
                  {{
                    q.skipped
                      ? $vuetify.lang.t(
                          "$vuetify.questionnaire_responses.skipped"
                        )
                      : $vuetify.lang.t(
                          "$vuetify.questionnaire_responses.not_answered"
                        )
                  }}
                </div>
              </v-card>
            </template>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions class="d-flex justify-end">
            <v-btn text color="error" @click="viewModal = false">
              {{ $vuetify.lang.t("$vuetify.common.close_btn") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
    <!-- View Modal end -->

    <!-- Edit Modal start -->
    <template v-if="editModal">
      <v-dialog v-model="editModal" max-width="820px" scrollable persistent>
        <v-card>
          <v-card-title class="response-details-card-title">
            <span class="headline">
              <strong>{{
                $vuetify.lang.t(
                  "$vuetify.questionnaire_responses.edit_response_title"
                )
              }}</strong>
            </span>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text style="max-height: 70vh">
            <template v-if="isDetailLoading">
              <Loader class="mr-2 mt-6" />
            </template>
            <v-form v-else ref="editForm" class="pt-4">
              <v-card
                v-for="q in editableQuestions"
                :key="q.question_key"
                outlined
                class="mb-4 question-card"
              >
                <v-card-text>
                  <!-- Question (read-only) -->
                  <div class="form-field question-text">
                    {{ q.question || q.question_key }}
                    <span v-if="q.required" class="error--text">*</span>
                  </div>

                  <!-- Text answer -->
                  <v-textarea
                    v-if="!q.type || q.type === 'text'"
                    v-model="editAnswers[q.question_key]"
                    :placeholder="q.placeholder || ''"
                    class="form-field"
                    outlined
                    dense
                    auto-grow
                    rows="1"
                    hide-details="auto"
                    :rules="q.required ? [requiredRule] : []"
                    @blur="trimAnswer(q.question_key)"
                  ></v-textarea>

                  <!-- Number answer -->
                  <v-text-field
                    v-else-if="q.type === 'number'"
                    v-model="editAnswers[q.question_key]"
                    type="number"
                    :placeholder="q.placeholder || ''"
                    class="form-field"
                    outlined
                    dense
                    hide-details="auto"
                    :rules="q.required ? [requiredRule] : []"
                    @blur="trimAnswer(q.question_key)"
                  ></v-text-field>

                  <!-- Radio answer -->
                  <v-radio-group
                    v-else-if="q.type === 'radio'"
                    v-model="editAnswers[q.question_key]"
                    class="mt-0 pt-0"
                    hide-details="auto"
                    :rules="q.required ? [requiredRule] : []"
                  >
                    <div
                      v-for="(optionText, optionIndex) in q.options"
                      :key="optionIndex"
                      class="option-row d-flex align-center"
                      style="cursor: pointer"
                      @click="editAnswers[q.question_key] = optionText"
                    >
                      <v-radio
                        :value="optionText"
                        class="ma-0 mr-3"
                        hide-details
                      ></v-radio>
                      <span class="option-field">{{ optionText }}</span>
                    </div>
                  </v-radio-group>

                  <!-- Checkbox answer -->
                  <div v-else-if="q.type === 'checkbox'">
                    <div
                      v-for="(optionText, optionIndex) in q.options"
                      :key="optionIndex"
                      class="option-row d-flex align-center"
                      style="cursor: pointer"
                      @click="toggleCheckbox(q.question_key, optionText)"
                    >
                      <v-checkbox
                        :input-value="isChecked(q.question_key, optionText)"
                        readonly
                        hide-details
                        class="ma-0 mr-3 pa-0"
                      ></v-checkbox>
                      <span class="option-field">{{ optionText }}</span>
                    </div>
                    <div
                      v-if="
                        q.required &&
                        (!editAnswers[q.question_key] ||
                          editAnswers[q.question_key].length === 0)
                      "
                      class="error--text answer-type-text mt-1"
                    >
                      {{
                        $vuetify.lang.t(
                          "$vuetify.questionnaire_responses.answer_required"
                        )
                      }}
                    </div>
                  </div>

                  <!-- Fallback: free text -->
                  <v-textarea
                    v-else
                    v-model="editAnswers[q.question_key]"
                    :placeholder="q.placeholder || ''"
                    class="form-field"
                    outlined
                    dense
                    auto-grow
                    rows="1"
                    hide-details="auto"
                    :rules="q.required ? [requiredRule] : []"
                    @blur="trimAnswer(q.question_key)"
                  ></v-textarea>
                </v-card-text>
              </v-card>
            </v-form>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions class="d-flex justify-end">
            <v-btn
              text
              color="error"
              :disabled="isSaving"
              @click="editModal = false"
            >
              {{ $vuetify.lang.t("$vuetify.common.cancel_btn") }}
            </v-btn>
            <v-btn
              color="primary"
              :loading="isSaving"
              :disabled="isDetailLoading"
              @click="saveResponse"
            >
              {{ $vuetify.lang.t("$vuetify.common.save_btn") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
    <!-- Edit Modal end -->

    <template v-if="fileGenerationModal">
      <FileGenerationModal
        :open="fileGenerationModal"
        :close-modal="() => { fileGenerationModal = false; }"
        :title="
          $vuetify.lang.t(
            '$vuetify.logs_file_generation.questionnaire_responses_file_generation_title'
          )
        "
        :download-request-txt="
          $vuetify.lang.t(
            '$vuetify.logs_file_generation.questionnaire_responses_download_request_txt'
          )
        "
        :file-generation-instruction-txt="
          $vuetify.lang.t(
            '$vuetify.logs_file_generation.generate_questionnaire_responses_instruction'
          )
        "
        :request-file-generation="questionnaireResponsesDownloadRequest"
        :prefix="maira_questionnaire_history_prefix"
        :history_type="HISTORY_TYPES.MAIRA_QUESTIONNAIRE"
        :filters="{
          start_date,
          end_date,
          selectedProfileIds,
          searchBySessionId,
          searchByUserId,
          searchByMemberId,
          search_text: searchText,
          selectedQuestionnaireIds,
        }"
      />
    </template>
    <template v-if="fileHistoryModal">
      <FileGeneratedHistoryModal
        :open="fileHistoryModal"
        :close-modal="() => { fileHistoryModal = false; }"
        :title="
          $vuetify.lang.t(
            '$vuetify.logs_file_generation.questionnaire_responses_file_generated_history_title'
          )
        "
        :prefix="maira_questionnaire_history_prefix"
        :history_type="HISTORY_TYPES.MAIRA_QUESTIONNAIRE"
      />
    </template>
  </v-card>
</template>

<script>
import { debounce } from "lodash";
import { mapActions, mapState } from "vuex";

import { ACTION_BUTTON_GROUP } from "@/components/common/buttongroup/_buttongrouptypes";
import ButtonGroup from "@/components/common/buttongroup/ButtonGroup";
import FileGeneratedHistoryModal from "@/components/common/FileGeneratedHistoryModal";
import FileGenerationModal from "@/components/common/FileGenerationModal";
import Loader from "@/components/common/loader/Loader";
import ThinScrollChip from "@/components/common/ThinScrollChip";
import { HISTORY_TYPES } from "@/global/_constants";
import { parseCheckboxOptions } from "@/helpers/checkboxParsing";
import { getLoggedInUserId } from "@/helpers/helper";
import {
  ADD_ALERT,
  GENERATE_MAIRA_QUESTIONNAIRE_HISTORY,
  GET_GPT_PROFILES,
  GET_QUESTIONNAIRE_RESPONSE_DETAIL,
  GET_QUESTIONNAIRE_RESPONSES,
  MAIRA_QUESTIONNAIRE,
  UPDATE_QUESTIONNAIRE_RESPONSE,
} from "@/store/_actiontypes";

const SKIP_SENTINELS = ["skip", "skip question", "skip this", "skipped"];

export default {
  components: {
    ButtonGroup,
    FileGenerationModal,
    FileGeneratedHistoryModal,
    Loader,
    ThinScrollChip,
  },
  props: {
    startDate: {
      type: String,
      required: false,
      default: null,
    },
    endDate: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      ACTION_BUTTON_GROUP: ACTION_BUTTON_GROUP,
      HISTORY_TYPES: HISTORY_TYPES,
      start_date: this.startDate,
      end_date: this.endDate,
      isLoading: false,
      responseList: [],
      searchText: "",
      searchByUserId: "",
      searchByMemberId: "",
      searchBySessionId: "",
      selectedQuestionnaires: [],
      selectedQuestionnaireIds: [],
      selectedProfiles: [],
      selectedProfileIds: [],
      page: 1,
      itemsPerPage: 5,
      sortBy: [],
      sortDesc: [],
      totalHits: 0,
      returnedHits: 0,
      questionnaires: [],
      isQuestionnaireListLoading: false,
      hasMoreQuestionnaires: false,
      questionnairePage: 0,
      questionnairePageSize: 100,
      gptProfiles: [],
      isProfileListLoading: false,
      hasMoreProfiles: false,
      profilePage: 0,
      profilePageSize: 100,
      viewModal: false,
      editModal: false,
      isDetailLoading: false,
      isSaving: false,
      detail: {},
      editableQuestions: [],
      editAnswers: {},
      originalAnswers: {},
      copiedField: { id: null, field: null },
      copyResetTimeout: null,
      fileGenerationModal: false,
      fileHistoryModal: false,
      maira_questionnaire_history_prefix: "maira_questionnaire_history_download",
      latestRequestId: 0,
      latestDetailRequestId: 0,
    };
  },
  computed: {
    ...mapState({
      selectedProject: state => state.project.selectedProject,
    }),
    filteredDetailQuestions() {
      if (!this.detail.questions) return [];
      return this.detail.questions.filter(q => {
        return !(q.answer === null && q.answered === false && q.skipped === false);
      });
    },
    responseListHeaders() {
      return [
        {
          text: this.$vuetify.lang.t(
            "$vuetify.questionnaire_responses.response_id"
          ),
          align: "start",
          sortable: false,
          value: "response_id",
        },
        {
          text: this.$vuetify.lang.t(
            "$vuetify.questionnaire_responses.questionnaire_name"
          ),
          align: "start",
          sortable: false,
          value: "questionnaire_name",
        },
        {
          text: this.$vuetify.lang.t(
            "$vuetify.questionnaire_responses.profile_name"
          ),
          align: "start",
          sortable: false,
          value: "profile_name",
        },
        {
          text: this.$vuetify.lang.t(
            "$vuetify.questionnaire_responses.user_name"
          ),
          align: "start",
          sortable: false,
          value: "user_name",
        },
        {
          text: this.$vuetify.lang.t(
            "$vuetify.questionnaire_responses.session_id"
          ),
          align: "start",
          sortable: false,
          value: "session_id",
        },
        {
          text: this.$vuetify.lang.t(
            "$vuetify.questionnaire_responses.created_at"
          ),
          align: "start",
          sortable: true,
          value: "created_at",
        },
        {
          text: this.$vuetify.lang.t(
            "$vuetify.questionnaire_responses.updated_at"
          ),
          align: "start",
          sortable: true,
          value: "updated_at",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.table_header.actions"),
          align: "center",
          sortable: false,
          value: "actions",
        },
      ];
    },
    sortState() {
      return JSON.stringify({
        sortBy: this.sortBy,
        sortDesc: this.sortDesc,
      });
    },
  },
  watch: {
    startDate(newVal) {
      this.start_date = newVal;
      this.page = 1;
      this.debouncedGetResponses();
    },
    endDate(newVal) {
      this.end_date = newVal;
      this.page = 1;
      this.debouncedGetResponses();
    },
    itemsPerPage() {
      this.page = 1;
      this.debouncedGetResponses();
    },
    page() {
      this.debouncedGetResponses();
    },
    searchText() {
      this.page = 1;
      this.debouncedGetResponses();
    },
    searchByUserId() {
      this.page = 1;
      this.debouncedGetResponses();
    },
    searchByMemberId() {
      this.page = 1;
      this.debouncedGetResponses();
    },
    searchBySessionId() {
      this.page = 1;
      this.debouncedGetResponses();
    },
    sortState() {
      this.page = 1;
      this.debouncedGetResponses();
    },
  },
  created() {
    this.debouncedGetResponses = debounce(this.getResponses, 500);
  },
  mounted() {
    this.page = 1;
    this.itemsPerPage = 5;
    this.debouncedGetResponses();
    this.getQuestionnaires();
    this.getGPTProfiles();
  },
  beforeDestroy() {
    this.debouncedGetResponses?.cancel();
    if (this.copyResetTimeout) {
      clearTimeout(this.copyResetTimeout);
      this.copyResetTimeout = null;
    }
  },
  methods: {
    ...mapActions("project", [
      GET_GPT_PROFILES,
      MAIRA_QUESTIONNAIRE,
      GENERATE_MAIRA_QUESTIONNAIRE_HISTORY,
      GET_QUESTIONNAIRE_RESPONSES,
      GET_QUESTIONNAIRE_RESPONSE_DETAIL,
      UPDATE_QUESTIONNAIRE_RESPONSE,
    ]),
    ...mapActions("alert", [ADD_ALERT]),
    applySessionStatus({ session_id: sessionId, session_status: sessionStatus } = {}) {
      if (!sessionId) return;
      this.responseList
        .filter(item => item.session_id === sessionId)
        .forEach(item => this.$set(item, "session_status", sessionStatus));
    },
    requiredRule(value) {
      return (
        (value !== null &&
          value !== undefined &&
          String(value).trim() !== "") ||
        this.$vuetify.lang.t("$vuetify.questionnaire_responses.answer_required")
      );
    },
    trimAnswer(questionKey) {
      const value = this.editAnswers[questionKey];
      if (value !== null && value !== undefined && typeof value === 'string') {
        this.$set(this.editAnswers, questionKey, value.trim());
      }
    },
    isChecked(questionKey, optionText) {
      const value = this.editAnswers[questionKey];
      return Array.isArray(value) && value.includes(optionText);
    },
    toggleCheckbox(questionKey, optionText) {
      const current = Array.isArray(this.editAnswers[questionKey])
        ? [...this.editAnswers[questionKey]]
        : [];
      const index = current.indexOf(optionText);
      if (index === -1) {
        current.push(optionText);
      } else {
        current.splice(index, 1);
      }
      this.$set(this.editAnswers, questionKey, current);
    },
    formatDate(datestr) {
      const date = new Date(datestr);
      if (isNaN(date.getTime())) return "N/A";
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      const hh = String(date.getHours()).padStart(2, "0");
      const min = String(date.getMinutes()).padStart(2, "0");
      const ss = String(date.getSeconds()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
    },
    formatAnswer(answer) {
      if (Array.isArray(answer)) {
        return answer.join(", ");
      }
      return answer;
    },
    copyToClipboard(item, text, field) {
      if (!text) return;
      if (!navigator.clipboard) {
        this[ADD_ALERT]({
          message: this.$vuetify.lang.t("$vuetify.common.copy_failed"),
          color: "error",
        });
        return;
      }
      navigator.clipboard.writeText(text).then(() => {
        this.copiedField = { id: item.response_id, field };
        if (this.copyResetTimeout) clearTimeout(this.copyResetTimeout);
        this.copyResetTimeout = setTimeout(() => {
          this.copiedField = { id: null, field: null };
        }, 1000);
      }).catch(() => {
        this[ADD_ALERT]({
          message: this.$vuetify.lang.t("$vuetify.common.copy_failed"),
          color: "error",
        });
      });
    },
    getResponses() {
      const params = {
        start:
          this.itemsPerPage !== -1 ? (this.page - 1) * this.itemsPerPage : 0,
        size: this.itemsPerPage !== -1 ? this.itemsPerPage : 10000,
      };
      if (this.searchText) {
        params.search_text = this.searchText.trim();
      }
      if (this.searchByUserId) {
        params.user_id = this.searchByUserId.trim();
      }
      if (this.searchByMemberId) {
        params.member_id = this.searchByMemberId.trim();
      }
      if (this.searchBySessionId) {
        params.session_id = this.searchBySessionId.trim();
      }
      if (this.selectedQuestionnaireIds?.length > 0) {
        params.questionnaire_ids = this.selectedQuestionnaireIds;
      }
      if (this.selectedProfileIds?.length > 0) {
        params.profile_ids = this.selectedProfileIds;
      }
      const sortByArr = Array.isArray(this.sortBy) ? this.sortBy : this.sortBy ? [this.sortBy] : [];
      const sortDescArr = Array.isArray(this.sortDesc) ? this.sortDesc : this.sortDesc ? [this.sortDesc] : [];
      if (sortByArr.length > 0) {
        params.sort_by = sortByArr.map((field, index) => ({
          field,
          order: sortDescArr?.[index] ? "desc" : "asc",
        }));
      }
      if (this.start_date) {
        params.start_date = this.start_date;
      }
      if (this.end_date) {
        params.end_date = this.end_date;
      }
      this.isLoading = true;
      const requestId = ++this.latestRequestId;
      this.GET_QUESTIONNAIRE_RESPONSES({
        project_id: this.selectedProject.id,
        params,
      })
        .then(response => {
          if (requestId !== this.latestRequestId) return;
          this.responseList = response.data?.responses || [];
          this.totalHits = response.data?.total_hits || 0;
          this.returnedHits = response.data?.returned_hits || 0;
        })
        .catch(_error => {
          if (requestId !== this.latestRequestId) return;
          this[ADD_ALERT]({
            message: this.$vuetify.lang.t(
              "$vuetify.questionnaire_responses.fetch_error"
            ),
            color: "error",
          });
        })
        .finally(() => {
          if (requestId === this.latestRequestId) {
            this.isLoading = false;
          }
        });
    },
    getQuestionnaires() {
      if (this.isQuestionnaireListLoading) return;
      this.isQuestionnaireListLoading = true;
      this.MAIRA_QUESTIONNAIRE({
        project_id: this.selectedProject.id,
        start: this.questionnairePage * this.questionnairePageSize,
        size: this.questionnairePageSize,
      })
        .then(res => {
          const newQuestionnaires =
            res?.data?.detail?.response?.questionnaires || [];
          const totalHits = res?.data?.detail?.response?.total_hits || 0;
          this.questionnaires =
            this.questionnairePage === 0
              ? newQuestionnaires
              : [...this.questionnaires, ...newQuestionnaires];
          this.hasMoreQuestionnaires = this.questionnaires.length < totalHits;
          this.questionnairePage++;
        })
        .catch(() => {
          this[ADD_ALERT]({
            message: this.$vuetify.lang.t(
              "$vuetify.questionnaire_responses.questionnaire_fetch_error"
            ),
            color: "error",
          });
        })
        .finally(() => {
          this.isQuestionnaireListLoading = false;
        });
    },
    getGPTProfiles() {
      if (this.isProfileListLoading) return;
      this.isProfileListLoading = true;
      this.GET_GPT_PROFILES({
        project_id: this.selectedProject.id,
        start: this.profilePage * this.profilePageSize,
        size: this.profilePageSize,
      })
        .then(res => {
          const newProfiles = res?.data?.detail?.response?.profiles || [];
          const totalHits = res?.data?.detail?.response?.total_hits || 0;
          this.gptProfiles =
            this.profilePage === 0
              ? newProfiles
              : [...this.gptProfiles, ...newProfiles];
          this.hasMoreProfiles = this.gptProfiles.length < totalHits;
          this.profilePage++;
        })
        .catch(() => {
          this[ADD_ALERT]({
            message: this.$vuetify.lang.t(
              "$vuetify.gpt_profile.profile_fetch_error"
            ),
            color: "error",
          });
        })
        .finally(() => {
          this.isProfileListLoading = false;
        });
    },
    handleQuestionnaireChange(selectedItems) {
      const items = Array.isArray(selectedItems) ? selectedItems : [];
      this.selectedQuestionnaires = items.filter(
        item => item && typeof item !== "string"
      );
      this.page = 1;
      this.selectedQuestionnaireIds = this.selectedQuestionnaires.map(
        item => item.id
      );
      this.debouncedGetResponses();
    },
    handleProfileChange(selectedItems) {
      const items = Array.isArray(selectedItems) ? selectedItems : [];
      this.selectedProfiles = items.filter(
        item => item && typeof item !== "string"
      );
      this.page = 1;
      this.selectedProfileIds = this.selectedProfiles.map(item => item.id);
      this.debouncedGetResponses();
    },
    removeQuestionnaire(item) {
      const index = this.selectedQuestionnaires.findIndex(q => q.id === item.id);
      if (index !== -1) {
        this.selectedQuestionnaires.splice(index, 1);
        this.handleQuestionnaireChange(this.selectedQuestionnaires);
      }
    },
    removeProfile(item) {
      const index = this.selectedProfiles.findIndex(p => p.id === item.id);
      if (index !== -1) {
        this.selectedProfiles.splice(index, 1);
        this.handleProfileChange(this.selectedProfiles);
      }
    },
    openViewModal(item) {
      this.viewModal = true;
      this.loadDetail(item.response_id);
    },
    openEditModal(item) {
      this.editModal = true;
      this.loadDetail(item.response_id, true);
    },
    loadDetail(responseId, forEdit = false) {
      this.isDetailLoading = true;
      this.detail = {};
      this.editableQuestions = [];
      this.editAnswers = {};
      this.originalAnswers = {};
      const requestId = ++this.latestDetailRequestId;
      this.GET_QUESTIONNAIRE_RESPONSE_DETAIL({
        project_id: this.selectedProject.id,
        response_id: responseId,
      })
        .then(response => {
          if (requestId !== this.latestDetailRequestId) return;
          this.detail = response.data || {};
          if (forEdit) {
            this.prepareEditState(this.detail.questions || []);
          }
        })
        .catch(_error => {
          if (requestId !== this.latestDetailRequestId) return;
          if (forEdit) {
            this.editModal = false;
          } else {
            this.viewModal = false;
          }
          this[ADD_ALERT]({
            message: this.$vuetify.lang.t(
              "$vuetify.questionnaire_responses.detail_fetch_error"
            ),
            color: "error",
          });
        })
        .finally(() => {
          if (requestId === this.latestDetailRequestId) {
            this.isDetailLoading = false;
          }
        });
    },
    prepareEditState(questions) {
      const editable = [];
      const answers = {};
      const originals = {};
      questions.forEach(q => {
        if (q.answer === null && q.answered === false && q.skipped === false) {
          return;
        }
        editable.push(q);
        if (q.type === "checkbox") {
          answers[q.question_key] = this.parseCheckboxAnswer(q.answer, q.options);
        } else {
          answers[q.question_key] =
            q.answer === null || q.answer === undefined ? "" : q.answer;
        }
        originals[q.question_key] = answers[q.question_key];
      });
      this.editableQuestions = editable;
      this.editAnswers = answers;
      this.originalAnswers = originals;
    },
    parseCheckboxAnswer(answer, options = []) {
      if (Array.isArray(answer)) {
        return answer.map(v => String(v).trim()).filter(v => v);
      }
      if (typeof answer === "string" && answer.trim()) {
        if (SKIP_SENTINELS.includes(answer.trim().toLowerCase())) {
          return [];
        }
        const trimmed = answer.trim();
        if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
          try {
            const parsed = JSON.parse(answer);
            if (Array.isArray(parsed)) {
              return parsed.map(v => String(v).trim()).filter(v => v);
            }
          } catch (_) {
            // fall through to comma-split
          }
        }
        return parseCheckboxOptions(trimmed, options);
      }
      return [];
    },
    serializeAnswer(question) {
      const value = this.editAnswers[question.question_key];
      if (question.type === "checkbox") {
        return Array.isArray(value) && value.length > 0 ? value.join(",") : "skipped";
      }
      // For text, number, and other types
      if (value === null || value === undefined) return "skipped";
      const trimmed = String(value).trim();
      return trimmed === "" ? "skipped" : trimmed;
    },
    hasAnswerChanged(question) {
      const original = this.originalAnswers[question.question_key];
      const current = this.editAnswers[question.question_key];
      if (question.type === "checkbox") {
        const a = Array.isArray(original) ? [...original].sort() : [];
        const b = Array.isArray(current) ? [...current].sort() : [];
        return JSON.stringify(a) !== JSON.stringify(b);
      }
      return String(original ?? "").trim() !== String(current ?? "").trim();
    },
    validateRequiredAnswers() {
      const missing = this.editableQuestions.filter(q => {
        if (!q.required) return false;
        const value = this.editAnswers[q.question_key];
        if (q.type === "checkbox") {
          return !Array.isArray(value) || value.length === 0;
        }
        return (
          value === null || value === undefined || String(value).trim() === ""
        );
      });
      return missing.length === 0;
    },
    async saveResponse() {
      const formValid = this.$refs.editForm
        ? this.$refs.editForm.validate()
        : true;
      if (!formValid || !this.validateRequiredAnswers()) {
        this[ADD_ALERT]({
          message: this.$vuetify.lang.t(
            "$vuetify.questionnaire_responses.validation_error"
          ),
          color: "error",
        });
        return;
      }

      const changedQuestions = this.editableQuestions.filter(q =>
        this.hasAnswerChanged(q)
      );
      if (changedQuestions.length === 0) {
        this.editModal = false;
        return;
      }

      const memberId = getLoggedInUserId();
      this.isSaving = true;
      try {
        const qa_pairs = changedQuestions.map(q => ({
          question_key: q.question_key,
          updated_value: this.serializeAnswer(q)
        }));

        await this.UPDATE_QUESTIONNAIRE_RESPONSE({
          project_id: this.selectedProject.id,
          response_id: this.detail.response_id,
          params: {
            qa_pairs: qa_pairs,
            member_id: memberId,
          },
        });

        changedQuestions.forEach(question => {
          const savedValue = this.editAnswers[question.question_key];
          this.$set(
            this.originalAnswers,
            question.question_key,
            question.type === "checkbox"
              ? Array.isArray(savedValue)
                ? [...savedValue]
                : []
              : savedValue
          );
        });
        this[ADD_ALERT]({
          message: this.$vuetify.lang.t(
            "$vuetify.questionnaire_responses.update_success"
          ),
          color: "success",
        });
        this.editModal = false;
        this.debouncedGetResponses();
      } catch (error) {
        this[ADD_ALERT]({
          message: this.$vuetify.lang.t(
            "$vuetify.questionnaire_responses.update_incomplete_error"
          ),
          color: "error",
        });
        this.debouncedGetResponses();
      } finally {
        this.isSaving = false;
      }
    },
    questionnaireResponsesDownloadRequest({
      project_id,
      start_date,
      end_date,
      language,
      profile_ids,
      questionnaire_ids,
      user_id,
      member_id,
      search_text,
      session_id,
    }) {
      return this.GENERATE_MAIRA_QUESTIONNAIRE_HISTORY({
        project_id,
        start_date,
        end_date,
        language,
        profile_ids,
        questionnaire_ids,
        user_id,
        member_id,
        search_text,
        session_id,
      });
    },
  },
};
</script>

<style scoped>
.response-details-card-title {
  height: auto !important;
  min-height: unset !important;
}
.member-id-text {
  font-size: 0.72rem;
}
@media (max-width: 959px) {
  ::v-deep .v-btn__content .v-icon--left {
    margin-left: 5px !important;
    margin-right: 5px !important;
  }
}
.answer-type-text {
  font-size: 0.72rem;
}
.question-card {
  border: 1px solid #e0e0e0;
}
.question-text {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 12px;
}
.form-field {
  font-size: 18px;
}
.option-row {
  margin-bottom: 8px;
}
.option-field {
  font-size: 18px;
}

.v-select.v-input--dense .v-chip {
  margin: 6px 4px 0px 4px;
}

.v-select.v-select--chips:not(.v-text-field--single-line).v-text-field--box.v-select--chips--small.v-input--dense >>> .v-select__selections:has(.v-chip),
.v-select.v-select--chips:not(.v-text-field--single-line).v-text-field--enclosed.v-select--chips--small.v-input--dense >>> .v-select__selections:has(.v-chip) {
  margin-top: 6px;
  margin-bottom: 6px;
}
</style>
